import { useQuery, useMutation, keepPreviousData } from "@tanstack/react-query";
import useAuthStore from "@/store/useAuthStore";
import {
  fetchRequestData,
  getActiveTenants,
  updateActiveTenantsById,
  getTenantsUsedData,
  getTenantsUsers,
  tenantProfileGet,
  tenantsUsageTracker,
  tenantsUsageTrackerByid,
  webScrapingAPI,
  tenantProfileCreate,
  approveRequest,
  rejectRequest,
  uploadTenantLogoAPI,
} from "@/api/adminDashboardService";
import { useQueryClient } from "@tanstack/react-query";
import type { TenantProfilePayload } from "@/types/tenantService";
import type { RequestQueryParams } from "@/api/adminDashboardService";

export interface WebScrapingPayload {
  id: string;
  linkedin: string;
  website: string;
}
export const useRequest = (params: RequestQueryParams) => {
  return useQuery({
    queryKey: ["requestData", params],
    queryFn: () => fetchRequestData(params),
    // keepPreviousData: true,
  });
};

export const useActiveTenants = (params?: { searchKeyword?: string }) => {
  return useQuery({
    queryKey: ["requestActiveAdminData", params],
    queryFn: () => getActiveTenants(params),
  });
};
export const useTenantsUsers = () => {
  return useQuery({
    queryKey: ["requestTenantsUserData"],
    queryFn: getTenantsUsers,
  });
};

export const useTenantsUsedData = (page: number, limit: number, searchKeyword?: string) => {
  return useQuery({
    queryKey: ["tenantsUsedData", page, limit, searchKeyword],
    queryFn: () => getTenantsUsedData(page, limit, searchKeyword),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
};

export const useTenantsUsageTracker = (filters: {
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ["tenantsUsageTracker", filters],
    queryFn: () => tenantsUsageTracker(filters),
    enabled: !!filters,
  });
};
export const useTenantsCountUsageTracker = (filters: {
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ["tenantsCountUsageTracker", filters],
    queryFn: () => tenantsUsageTracker(filters),
    enabled: !!filters,
  });
};
export const useTenantsUsageTrackerById = (params: {
  id: string;
  userId?: string;
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ["tenantsUsageTrackerById", params],
    queryFn: () => tenantsUsageTrackerByid(params),
  });
};
export const useTenantsDataUsageTrackerById = (params: {
  id: string;
  type: string;
  year?: number;
  fromDate?: string;
  toDate?: string;
}) => {
  return useQuery({
    queryKey: ["tenantsDataUsageTrackerById", params],
    queryFn: () => tenantsUsageTrackerByid(params),
  });
};

export const useWebScrapingAPI = () => {
  return useMutation({
    mutationFn: (payload: WebScrapingPayload) => webScrapingAPI(payload),
  });
};
export const useTenantProfileUpdate = () => {
  return useMutation({
    mutationFn: ({
      payload,
    }: {
      id: string;
      payload: TenantProfilePayload;
    }) => {
      if (!payload?.tenantId) {
        throw new Error("tenantId is required");
      }
      return tenantProfileCreate(payload.tenantId, payload);
    },
  });
};

export const useTenantProfileCreate = () => {
  return useMutation({
    mutationFn: (payload: TenantProfilePayload) => {
      if (!payload?.tenantId) {
        throw new Error("tenantId is required");
      }
      return tenantProfileCreate(payload.tenantId, payload);
    },
  });
};

export const useTenantProfileGet = (params: {
  tenantId: string;
  include?: string;
}) => {
  return useQuery({
    queryKey: ["tenantprofileGet", params],
    queryFn: ({ queryKey }) => {
      const [, actualParams] = queryKey as [
        string,
        { tenantId: string; include?: string },
      ];
      return tenantProfileGet(actualParams);
    },
    enabled: !!params?.tenantId,
  });
};

export const useUpdateActiveTenantsById = () => {
  return useMutation({
    mutationFn: ({
      payload,
      id,
    }: {
      payload: Record<string, string | boolean>;
      id: string;
    }) => {
      const restPayload = Object.fromEntries(
        Object.entries(payload).filter(([key]) => key !== "status"),
      );
      return updateActiveTenantsById(restPayload, id);
    },
  });
};

export const useApproveRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requestData"] });
    },
  });
};

export const useRejectRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rejectRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["requestData"] });
    },
  });
};



export const useUploadTenantLogoAPI = () => {
  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      uploadTenantLogoAPI(id, formData),
  });
};

export const useAzureCostData = () => {
  const role = useAuthStore((state) => state.role);
  return useQuery({
    queryKey: ["azureCostData"],
    queryFn: () => import("@/api/adminDashboardService").then(m => m.fetchAzureCostAPI()),
    staleTime: 6 * 60 * 60 * 1000, // 6 hours
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: role === "SUPER_ADMIN"
  });
};
